import { useEffect, type RefObject } from "react"
import { TrackLane } from "@/components/shot-panel/timeline-editor/track-lane"
import type { TimelineAudioAsset } from "@/components/shot-panel/timeline-editor/types"
import { Playhead, TimelineRuler, useTimelineZoom } from "@/components/timeline-player/timeline-chrome"
import type { PlaybackTimeStore, TimelineDocument, TimelineSource, TimelineTrack } from "@/lib/timeline"
import { cn } from "@/lib/utils"

/** Как у TimelinePlayer: колонка подписей дорожек. */
export const TIMELINE_LANE_HEADER_W = 140

const EMPTY_SELECTED_IDS: readonly string[] = []
const EMPTY_LOCKED = new Set<string>()

export type TimelineLanesMetrics = {
  pxPerMs: number
}

type TimelineLanesProps = {
  doc: TimelineDocument
  timelineSources: Record<string, TimelineSource>
  assets?: TimelineAudioAsset[]
  animaticSource?: TimelineSource | null
  durationMs: number
  showTrackHeaders?: boolean
  /** Ширина подписи дорожки. 0, если заголовки скрыты. */
  headerW?: number
  resetKey?: string | number | null
  timeStore: PlaybackTimeStore
  playingRef: RefObject<boolean>
  selectedIdsByTrack: Map<string, readonly string[]>
  onSeek: (ms: number) => void
  onScrubStart?: () => void
  onScrubEnd?: () => void
  onSelectClip: (trackId: string, clipId: string, additive: boolean) => void
  onMoveClip: (trackId: string, clipId: string, newStartMs: number) => void
  onResizeClip: (trackId: string, clipId: string, edge: "left" | "right", newEdgeMs: number) => void
  onDragEnd: () => void
  onClearSelection?: () => void
  trackLabel?: (track: TimelineTrack) => string
  fillHeight?: boolean
  className?: string
  /** Текущий px/ms для снаппинга снаружи (драг клипов в TimelinePlayer). */
  metricsRef?: RefObject<TimelineLanesMetrics>
  /** false — не сдвигать дорожки за плейхедом (ролик уже вписан в ширину). */
  scrollWithPlayhead?: boolean
}

export function TimelineLanes({
  doc,
  timelineSources,
  assets = [],
  animaticSource = null,
  durationMs,
  showTrackHeaders = true,
  headerW = TIMELINE_LANE_HEADER_W,
  resetKey,
  timeStore,
  playingRef,
  selectedIdsByTrack,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onSelectClip,
  onMoveClip,
  onResizeClip,
  onDragEnd,
  onClearSelection,
  trackLabel,
  fillHeight = false,
  className,
  metricsRef,
  scrollWithPlayhead = true,
}: TimelineLanesProps) {
  const labelW = showTrackHeaders ? headerW : 0
  const { pxPerMs, lanesRef, setLanesRef, laneContentPx } = useTimelineZoom({
    durationMs,
    headerW: labelW,
    resetKey,
  })

  useEffect(() => {
    if (!metricsRef) return
    metricsRef.current.pxPerMs = pxPerMs
  }, [metricsRef, pxPerMs])

  return (
    <div
      ref={setLanesRef}
      className={cn(
        "w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        fillHeight ? "pb-0" : "pb-3",
        className,
      )}
      onPointerDown={() => onClearSelection?.()}
    >
      <div className="relative flex flex-col gap-1" style={{ width: pxPerMs > 0 ? labelW + laneContentPx : undefined }}>
        <div className="flex items-end gap-0" onPointerDown={(event) => event.stopPropagation()}>
          {showTrackHeaders ? (
            <div className="sticky left-0 z-40 shrink-0 self-stretch bg-ui-bg-elevated" style={{ width: labelW }} />
          ) : null}
          <TimelineRuler
            pxPerMs={pxPerMs}
            durationMs={durationMs}
            onSeek={onSeek}
            onScrubStart={onScrubStart}
            onScrubEnd={onScrubEnd}
          />
        </div>

        {doc.tracks.map((track) => (
          <div key={track.id} className="flex items-stretch gap-0">
            {showTrackHeaders ? (
              <div
                className="sticky left-0 z-40 flex shrink-0 items-center bg-ui-bg-elevated pr-1"
                style={{ width: labelW }}
              >
                <span className="truncate px-2 text-[11px] font-medium text-ui-text-secondary">
                  {trackLabel?.(track) ?? track.kind}
                </span>
              </div>
            ) : null}
            <TrackLane
              track={track}
              pxPerMs={pxPerMs}
              selectedClipIds={selectedIdsByTrack.get(track.id) ?? EMPTY_SELECTED_IDS}
              lockedClipIds={EMPTY_LOCKED}
              sources={timelineSources}
              animaticSource={animaticSource}
              assets={assets}
              onSelectClip={onSelectClip}
              onMoveClip={onMoveClip}
              onResizeClip={onResizeClip}
              onDragEnd={onDragEnd}
            />
          </div>
        ))}

        {pxPerMs > 0 ? (
          <Playhead
            store={timeStore}
            pxPerMs={pxPerMs}
            offsetPx={labelW}
            playingRef={playingRef}
            scrollRef={lanesRef}
            scrollWithPlayhead={scrollWithPlayhead}
          />
        ) : null}
      </div>
    </div>
  )
}
